'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { TechJob } from './technicianTypes';

interface Props {
  job: TechJob;
  onComplete: (data: {
    paymentMode: TechJob['paymentMode'];
    filters: TechJob['filters'];
    actualStartTime: string;
    actualEndTime: string;
  }) => void;
}

interface FormData {
  paymentMode: TechJob['paymentMode'];
  actualStartTime: string;
  actualEndTime: string;
}

export default function JobCompletionForm({ job, onComplete }: Props) {
  const [filters, setFilters] = useState<TechJob['filters']>({ ...job.filters });
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      paymentMode: job.paymentMode,
      actualStartTime: '',
      actualEndTime: '',
    },
  });

  function toggleFilter(key: keyof TechJob['filters']) {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function onSubmit(data: FormData) {
    setLoading(true);
    // Backend: PATCH /api/appointments/:id/complete with completion data
    // This triggers automatic filter inventory deduction for checked filters
    setTimeout(() => {
      onComplete({ paymentMode: data.paymentMode, filters, actualStartTime: data.actualStartTime, actualEndTime: data.actualEndTime });
      toast.success(`Job completed for ${job.customerName} — filters logged, inventory updated`);
      setLoading(false);
    }, 900);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <p className="text-sm font-700 text-foreground">Complete Job Log</p>

      {/* Filters replaced */}
      <div>
        <label className="block text-xs font-600 text-foreground mb-1.5">
          Filters Replaced <span className="text-muted-foreground font-400">(tap to toggle — inventory deducted automatically)</span>
        </label>
        <div className="flex gap-2 flex-wrap">
          {(['sedi', 'pre', 'mem', 'post'] as const).map((f) => {
            const labels: Record<string, string> = {
              sedi: 'Sedi', pre: 'Pre', mem: 'Mem', post: 'Post',
            };
            return (
              <button
                key={`jcf-${job.id}-${f}`}
                type="button"
                onClick={() => toggleFilter(f)}
                className={`px-4 py-2 rounded-xl text-sm font-700 border-2 transition-all duration-150 ${
                  filters[f]
                    ? 'bg-primary border-primary text-primary-foreground scale-105'
                    : 'bg-card border-border text-muted-foreground hover:border-primary/50'
                }`}
              >
                {labels[f]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Payment Mode */}
      <div>
        <label className="block text-xs font-600 text-foreground mb-1">
          Payment Mode Collected <span className="text-danger">*</span>
        </label>
        <select
          {...register('paymentMode', { required: 'Payment mode is required' })}
          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="Cash">Cash</option>
          <option value="Check">Check</option>
          <option value="BDO">BDO Bank Transfer</option>
          <option value="GCash">GCash</option>
        </select>
        {errors.paymentMode && <p className="mt-1 text-xs text-danger">{errors.paymentMode.message}</p>}
      </div>

      {/* Actual times */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-600 text-foreground mb-1">
            Actual Start Time <span className="text-danger">*</span>
          </label>
          <input
            type="time"
            {...register('actualStartTime', { required: 'Start time is required' })}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.actualStartTime && <p className="mt-1 text-xs text-danger">{errors.actualStartTime.message}</p>}
        </div>
        <div>
          <label className="block text-xs font-600 text-foreground mb-1">
            Actual End Time <span className="text-danger">*</span>
          </label>
          <input
            type="time"
            {...register('actualEndTime', { required: 'End time is required' })}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.actualEndTime && <p className="mt-1 text-xs text-danger">{errors.actualEndTime.message}</p>}
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 text-sm font-700 bg-success text-white rounded-xl flex items-center justify-center gap-2 hover:bg-green-700 transition-colors disabled:opacity-60"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Logging completion...
          </>
        ) : (
          <>
            <CheckCircle size={16} />
            Mark Job Complete
          </>
        )}
      </button>
    </form>
  );
}